import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import type { Theme } from 'hephaestus/core';
import { Box, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useFormatMoney } from '../hooks/useFormatMoney';
import {
  ALIGNED_TABLE_SX,
  BOLD_SX,
  BORDERED_BOX_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  TABLE_HEAD_SX,
} from '../styles';
import type { AccountBreakdownRow } from '../utils/portfolioRevampBuilders';
export type PortfolioRowDetailAccountBreakdownProps = {
  accountBreakdownRows: AccountBreakdownRow[];
  theme: Theme;
};

export const PortfolioRowDetailAccountBreakdown = ({
  accountBreakdownRows,
  theme,
}: PortfolioRowDetailAccountBreakdownProps) => {
  const { t } = useTranslation('portfolioTradingHub');
  const formatMoney = useFormatMoney();

  return (
    <Box
      sx={{
        ...BORDERED_BOX_SX,
        bgcolor: theme.color.surface,
        p: 1.5,
        overflow: 'hidden',
      }}
    >
      <Typography
        variant='labelSmall'
        component='div'
        sx={{ mb: 1, ...SECTION_HEADER_SX }}
      >
        {t('rowDetail.accountBreakdown.title', {
          count: accountBreakdownRows.length,
        })}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          sx={{
            backgroundColor: theme.color.surface,
            ...ALIGNED_TABLE_SX,
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: theme.color.background }}>
              {[
                t('rowDetail.accountBreakdown.type'),
                t('rowDetail.accountBreakdown.client'),
                t('rowDetail.accountBreakdown.marketValue'),
                t('rowDetail.accountBreakdown.cashAvailable'),
                t('rowDetail.accountBreakdown.pendingWithdraw'),
              ].map((label) => (
                <TableHeadCell
                  key={label}
                  sx={{
                    padding: '6px 12px',
                    ...TABLE_HEAD_SX,
                    fontWeight: 700,
                  }}
                >
                  {label}
                </TableHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {accountBreakdownRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ padding: '4px 12px' }}>
                  <Typography variant='bodySmall' sx={BOLD_SX}>
                    {row.type}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '4px 12px' }}>
                  <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
                    {row.clientName}
                  </Typography>
                </TableCell>
                <TableCell number sx={{ padding: '4px 12px' }}>
                  {formatMoney(row.marketValueCents)}
                </TableCell>
                <TableCell number sx={{ padding: '4px 12px' }}>
                  {formatMoney(row.cashAvailableForTradeCents)}
                </TableCell>
                <TableCell number sx={{ padding: '4px 12px' }}>
                  {row.pendingWithdrawCents > 0
                    ? formatMoney(row.pendingWithdrawCents)
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
